import { DateHelpers, groupConsecutiveDaysForNotes } from '@/helpers/date-helpers';
import { AI_NOTES_THRESHOLDS, ALERT_MESSAGE_TEMPLATES } from '../config/thresholds';

export interface KeyMetric {
  stay_date: string;
  capacity: number;
  ooo: number;
  /**
   * Change in sold rooms over the snapshot lookback window. Carries `delta_1day`
   * normally, but `delta_3day` for Monday meetings — see isMondayMeeting.
   */
  delta_1day: number;
}

/**
 * Δ1D/Δ3D are lookbacks on the *snapshot* date, not the stay date. On a Monday
 * the previous snapshot the team saw was Friday's, so Δ1D would hide everything
 * booked over the weekend for every stay date in the window. Monday meetings use
 * Δ3D instead. Matches the Portfolio hotel widget (features/portfolio/components/hotel-widget.tsx).
 */
export function isMondayMeeting(meetingDate?: string): boolean {
  return DateHelpers.dow(meetingDate) === 'Mon';
}

export interface KeyMetricsAlert {
  hasAlert: boolean;
  alertMessage?: string;
}

export interface ProcessedKeyMetrics {
  ooo: KeyMetricsAlert;
  delta_1day: KeyMetricsAlert;
}

/**
 * Pre-calculates key metrics alerts and formatted messages
 * Removes all math from LLM prompts by doing calculations in code
 */
export function calculateKeyMetricsAlerts(
  metrics: KeyMetric[],
  isMonday = false,
): ProcessedKeyMetrics {
  const result: ProcessedKeyMetrics = {
    ooo: { hasAlert: false },
    delta_1day: { hasAlert: false },
  };

  if (!metrics || metrics.length === 0) {
    return result;
  }

  // Process OOO rooms alerts. Every day with OOO rooms is reported regardless of how
  // small a share of capacity it is (RM-2324) — a percentage gate meant identical room
  // counts surfaced at small hotels but were dropped at large ones, which read as
  // inconsistent data.
  const oooAlerts = metrics
    .map((metric) => ({
      date: metric.stay_date,
      value: metric.ooo,
    }))
    .filter((item) => item.value > 0); // Only include days with actual OOO rooms

  if (oooAlerts.length > 0) {
    const groupedOoo = groupConsecutiveDaysForNotes(oooAlerts);
    result.ooo = {
      hasAlert: true,
      alertMessage: ALERT_MESSAGE_TEMPLATES.OOO_ROOMS.replace('{details}', groupedOoo.join(', ')),
    };
  }

  // Process delta 1-day alerts
  const deltaAlerts = metrics
    .map((metric) => {
      const absChange = Math.abs(metric.delta_1day);
      const percentage = (absChange / metric.capacity) * 100;
      const exceedsThreshold = percentage > AI_NOTES_THRESHOLDS.KEY_METRICS_ALERT_PERCENTAGE * 100;
      return {
        date: metric.stay_date,
        value: metric.delta_1day > 0 ? `+${metric.delta_1day}` : metric.delta_1day.toString(),
        exceedsThreshold,
        percentage,
      };
    })
    .filter((item) => item.exceedsThreshold);

  if (deltaAlerts.length > 0) {
    const groupedDelta = groupConsecutiveDaysForNotes(deltaAlerts);
    const template = isMonday
      ? ALERT_MESSAGE_TEMPLATES.SINCE_FRIDAY_CHANGE
      : ALERT_MESSAGE_TEMPLATES.DAILY_CHANGE;
    result.delta_1day = {
      hasAlert: true,
      alertMessage: template.replace('{details}', groupedDelta.join(', ')),
    };
  }

  return result;
}
